import { Tab, TabGroup, TabList, TabPanels, TabPanel } from '@headlessui/react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { Form, Formik } from 'formik';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import Button from '@dashboard/components/Button';
import CanUseFeature from '@dashboard/components/CanUseFeature';
import { FormElement, Label } from '@dashboard/components/Form';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import { Checkbox, TextInput } from '@dashboard/components/Formik';
import FormError, { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import Spinner from '@dashboard/components/Spinner';
import TabButton from '@dashboard/components/TabButton';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { usePreview } from '@dashboard/context/PreviewContext';
import Features from '@dashboard/enums/features';
import usePageQueries from '@dashboard/hooks/queries/usePageQueries';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import ConfirmationCheckoutMessageEditor from './ConfirmationCheckoutMessageEditor';
import usePage from '@dashboard/hooks/usePage';
import { PAGE_TYPES } from '@dashboard/enums/page-types';

function getDefaultConfirmationEmailSubject(page) {
  if (page.type === PAGE_TYPES.FORM) {
    return `Submission confirmation`;
  } else if (page.type === PAGE_TYPES.CHECKOUT) {
    const { product } = page;

    if (product.type === 'subscription') {
      return `Subscription confirmation for ${product.title}`;
    } else {
      return `${product.price <= 0 ? 'Submission' : 'Payment'} confirmation for ${product.title}`;
    }
  } else if (page.type === PAGE_TYPES.EVENT) {
    return `Booking confirmation for ${page.title}`;
  } else {
    return '';
  }
}

function getTabIndex({ sendEmailConfirmation, customizeEmailConfirmation }) {
  if (!sendEmailConfirmation) {
    return 2;
  }

  if (customizeEmailConfirmation) return 1;

  return 0;
}

const EmailConfirmationForm = () => {
  const { page, updatePage } = usePage();

  const [, previewDispatch] = usePreview();
  const setPreview = value => previewDispatch({ type: 'set', value });

  const defaultConfirmationEmailSubject = getDefaultConfirmationEmailSubject(page);

  useEffect(() => {
    if (page.sendEmailConfirmation) {
      setPreview({
        type: 'confirmationEmailMessage',
      });
    }

    return () =>
      setPreview({
        type: 'checkout',
      });
  }, []);

  const pageValues = {
    sendEmailConfirmation: page.sendEmailConfirmation,
    customizeEmailConfirmation: page.customizeEmailConfirmation,
    confirmationEmailSubject: page.confirmationEmailSubject || defaultConfirmationEmailSubject,
    confirmationEmailMessage: page.confirmationEmailMessage,
    confirmationEmailShowLogo: page.confirmationEmailShowLogo,
    confirmationEmailShowStoreName: page.confirmationEmailShowStoreName,
  };

  return (
    <Formik
      initialValues={pageValues}
      onSubmit={async (values, actions) => {
        actions.setStatus(undefined);

        const { data, error } = await updatePage.mutateAsync(values);

        if (data) {
          if (data.sendEmailConfirmation) {
            setPreview({
              type: 'confirmationEmailMessage',
            });
          } else {
            setPreview({
              type: 'checkout',
            });
          }

          actions.setStatus('');
          actions.resetForm({
            values: {
              sendEmailConfirmation: data.sendEmailConfirmation,
              confirmationEmailSubject: data.confirmationEmailSubject,
              confirmationEmailMessage: data.confirmationEmailMessage,
              customizeEmailConfirmation: data.customizeEmailConfirmation,
              confirmationEmailShowLogo: data.confirmationEmailShowLogo,
              confirmationEmailShowStoreName: data.confirmationEmailShowStoreName,
            },
          });
        } else {
          const { status, errors } = transformApiErrorToFormik(error);

          if (!isEmpty(errors)) {
            actions.setErrors(errors);
          } else {
            actions.setStatus(status);
          }
        }
      }}
    >
      {({ values, status, dirty, setFieldValue, handleSubmit, setValues, isSubmitting }) => {
        const tabIndex = getTabIndex(values);

        return (
          <Form onSubmit={handleSubmit}>
            <TabGroup
              defaultIndex={tabIndex}
              onChange={index => {
                switch (index) {
                  case 0:
                    setValues({
                      ...pageValues,
                      sendEmailConfirmation: true,
                      customizeEmailConfirmation: false,
                    });
                    break;
                  case 1:
                    setValues({
                      ...pageValues,
                      sendEmailConfirmation: true,
                      customizeEmailConfirmation: true,
                    });
                    break;
                  case 2:
                    setValues({
                      ...pageValues,
                      sendEmailConfirmation: false,
                      customizeEmailConfirmation: false,
                    });
                    break;
                }
              }}
            >
              <TabList className="flex mt-1 gap-x-2">
                <Tab className="w-1/3 outline-none">
                  {({ selected }) => <TabButton selected={selected}>Email confirmation</TabButton>}
                </Tab>
                <Tab className="w-1/3 outline-none">
                  {({ selected }) => <TabButton selected={selected}>Custom email</TabButton>}
                </Tab>
                <Tab className="w-1/3 outline-none">
                  {({ selected }) => <TabButton selected={selected}>No email</TabButton>}
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel className="mt-4">
                  <FormElement className="space-y-2">
                    <Label>Show on email</Label>
                    <Checkbox.Wrapper>
                      <Checkbox name="confirmationEmailShowLogo" id="confirmationEmailShowLogo" />
                      <Checkbox.Label htmlFor="confirmationEmailShowLogo">Logo</Checkbox.Label>
                    </Checkbox.Wrapper>

                    <Checkbox.Wrapper>
                      <Checkbox
                        name="confirmationEmailShowStoreName"
                        id="confirmationEmailShowStoreName"
                      />
                      <Checkbox.Label htmlFor="confirmationEmailShowStoreName">
                        Store name
                      </Checkbox.Label>
                    </Checkbox.Wrapper>
                  </FormElement>

                  <FormElement>{status && status}</FormElement>

                  <FormSubmit>
                    <div className="flex flex-wrap gap-y-4 justify-between w-full">
                      <div className="flex items-baseline whitespace-nowrap gap-x-2">
                        <FormSubmit.Button>Save</FormSubmit.Button>
                        <FormSubmit.State visible={!!dirty && !isSubmitting} />
                      </div>

                      <div className="ml-auto flex gap-x-2 items-start">
                        <SendTestEmailButton page={page} />
                      </div>
                    </div>
                  </FormSubmit>
                </TabPanel>

                <TabPanel className="mt-4">
                  <CanUseFeature feature={Features.CUSTOMIZE_EMAIL_CONFIRMATION}>
                    <FormElement>
                      <Label htmlFor="confirmationEmailSubject">Subject</Label>
                      <TextInput
                        required
                        id="confirmationEmailSubject"
                        name="confirmationEmailSubject"
                        type="text"
                        value={values.confirmationEmailSubject}
                      />
                      <FormError name="confirmationEmailSubject" />
                    </FormElement>

                    <FormElement>
                      <div className="flex justify-between items-baseline">
                        <Label htmlFor="confirmationEmailMessage" tags={['supports HTML']}>
                          Email body
                        </Label>
                        <span className="text-xs text-gray-500 font-normal text-right ml-auto">
                          Insert variables by typing{' '}
                          <code className="font-mono bg-gray-100 px-1 py-0.5 text-chocolate-1000">
                            {'{'}
                          </code>
                        </span>
                      </div>

                      <ConfirmationCheckoutMessageEditor
                        required
                        id="confirmationEmailMessage"
                        name="confirmationEmailMessage"
                        initialValue="Thank you for your payment"
                        page={page}
                        values={values}
                        setFieldValue={setFieldValue}
                      />
                      <FormError name="confirmationEmailMessage" />
                    </FormElement>

                    <FormElement className="space-y-2">
                      <Label>Show on email</Label>
                      <Checkbox.Wrapper>
                        <Checkbox name="confirmationEmailShowLogo" id="confirmationEmailShowLogo" />
                        <Checkbox.Label htmlFor="confirmationEmailShowLogo">Logo</Checkbox.Label>
                      </Checkbox.Wrapper>

                      <Checkbox.Wrapper>
                        <Checkbox
                          name="confirmationEmailShowStoreName"
                          id="confirmationEmailShowStoreName"
                        />
                        <Checkbox.Label htmlFor="confirmationEmailShowStoreName">
                          Store name
                        </Checkbox.Label>
                      </Checkbox.Wrapper>
                    </FormElement>

                    <FormElement>{status && status}</FormElement>

                    <FormSubmit>
                      <div className="flex flex-wrap gap-y-4 justify-between w-full">
                        <div className="flex items-baseline whitespace-nowrap gap-x-2">
                          <FormSubmit.Button>Save</FormSubmit.Button>
                          <FormSubmit.State visible={!!dirty && !isSubmitting} />
                        </div>

                        <div className="ml-auto flex gap-x-2 items-start">
                          <SendTestEmailButton page={page} />
                        </div>
                      </div>
                    </FormSubmit>
                  </CanUseFeature>
                </TabPanel>

                <TabPanel>
                  <FormSubmit>
                    <FormSubmit.Button>Save</FormSubmit.Button>
                    <FormSubmit.State visible={!!dirty && !isSubmitting} />
                  </FormSubmit>
                </TabPanel>
              </TabPanels>
            </TabGroup>
          </Form>
        );
      }}
    </Formik>
  );
};

function SendTestEmailButton({ page }) {
  const { user } = useAuth();
  const { sendTestEmail } = usePageQueries();
  const [testEmailState, setTestEmailState] = useState('initial'); // initial, sending, sent

  async function handleSendTestEmail() {
    setTestEmailState('sending');
    const sent = await sendTestEmail(page._id);

    if (!sent) {
      setTestEmailState('error');
      return;
    }

    setTestEmailState('sent');
    window.setTimeout(() => {
      setTestEmailState('initial');
    }, 2000);
  }

  return (
    <div className="flex gap-x-2 ml-auto items-start">
      <div className="flex flex-col">
        <Button
          type="button"
          className="ml-auto"
          onClick={handleSendTestEmail}
          appearance="light"
          disabled={testEmailState === 'sending' || testEmailState === 'sent'}
        >
          {(testEmailState === 'initial' || testEmailState === 'error') && 'Send test email'}

          {testEmailState === 'sending' && (
            <>
              Sending test email <Spinner className="ml-2 h-5 w-5" />
            </>
          )}

          {testEmailState === 'sent' && (
            <>
              Email sent <CheckCircleIcon className="ml-2 h-5 w-5" />
            </>
          )}
        </Button>

        <div className="text-right text-gray-800 mt-2 truncate w-64">
          {testEmailState === 'error' && (
            <FormErrorBase>Failed to send email. Please try again.</FormErrorBase>
          )}

          <small>
            Sending to <strong>{user.email}</strong>
          </small>
        </div>
      </div>
    </div>
  );
}

export default EmailConfirmationForm;
